/** Arpeggiator — transforms held chords into arpeggiated note patterns.
 *
 * Takes a set of held notes and cycles through them according to a pattern,
 * rate, and octave range. Supports latch mode where notes continue playing
 * after keys are released.
 *
 * Inspired by Logic Pro's arpeggiator patterns and interface.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Arpeggiator pattern modes. */
export type ArpPattern =
	| "up"
	| "down"
	| "up-down"
	| "down-up"
	| "random"
	| "as-played";

/** Arpeggiator rate as a musical note division. */
export type ArpRate =
	| "1/4"
	| "1/4t"
	| "1/8"
	| "1/8t"
	| "1/16"
	| "1/16t"
	| "1/32";

/** Arpeggiator parameters. */
export type ArpParams = {
	/** Whether the arpeggiator is active. */
	readonly enabled: boolean;
	/** Arpeggio pattern. */
	readonly pattern: ArpPattern;
	/** Musical rate/division for stepping through notes. */
	readonly rate: ArpRate;
	/** Number of octaves to arpeggiate across (1–4). */
	readonly octaves: number;
	/** Latch mode — notes keep arpeggiating after release. */
	readonly latch: boolean;
	/** Gate length as fraction of step duration (0.1–1.0). */
	readonly gate: number;
};

/** Default arpeggiator parameters. */
export const DEFAULT_ARP: ArpParams = {
	enabled: false,
	pattern: "up",
	rate: "1/8",
	octaves: 1,
	latch: false,
	gate: 0.8,
} as const;

/** All available arpeggiator patterns. */
export const ARP_PATTERNS: readonly ArpPattern[] = [
	"up",
	"down",
	"up-down",
	"down-up",
	"random",
	"as-played",
] as const;

/** All available arpeggiator rates. */
export const ARP_RATES: readonly ArpRate[] = [
	"1/4",
	"1/4t",
	"1/8",
	"1/8t",
	"1/16",
	"1/16t",
	"1/32",
] as const;

/** Display labels for patterns. */
export const ARP_PATTERN_LABELS: Readonly<Record<ArpPattern, string>> = {
	up: "Up",
	down: "Down",
	"up-down": "Up/Down",
	"down-up": "Down/Up",
	random: "Random",
	"as-played": "As Played",
} as const;

/** Display labels for rates. */
export const ARP_RATE_LABELS: Readonly<Record<ArpRate, string>> = {
	"1/4": "1/4",
	"1/4t": "1/4T",
	"1/8": "1/8",
	"1/8t": "1/8T",
	"1/16": "1/16",
	"1/16t": "1/16T",
	"1/32": "1/32",
} as const;

/** Beat multipliers for each rate. */
const RATE_MULTIPLIERS: Readonly<Record<ArpRate, number>> = {
	"1/4": 1.0,
	"1/4t": 2 / 3,
	"1/8": 0.5,
	"1/8t": 1 / 3,
	"1/16": 0.25,
	"1/16t": 1 / 6,
	"1/32": 0.125,
} as const;

// ---------------------------------------------------------------------------
// Arpeggiator engine
// ---------------------------------------------------------------------------

/** Callback for triggering notes. */
export type ArpNoteCallback = {
	readonly noteOn: (note: number) => void;
	readonly noteOff: (note: number) => void;
};

/** Build the sequence of MIDI notes for a given pattern, held notes, and octave range. */
function buildSequence(
	heldNotes: readonly number[],
	pattern: ArpPattern,
	octaves: number,
): readonly number[] {
	if (heldNotes.length === 0) return [];

	// Sort notes ascending for deterministic patterns
	const sorted = [...heldNotes].sort((a, b) => a - b);

	// Expand across octaves
	const expanded: number[] = [];
	for (let oct = 0; oct < octaves; oct++) {
		for (const note of sorted) {
			expanded.push(note + oct * 12);
		}
	}

	switch (pattern) {
		case "up":
			return expanded;
		case "down":
			return [...expanded].reverse();
		case "up-down": {
			if (expanded.length <= 1) return expanded;
			// Up, then down skipping first and last to avoid repeats
			const down = [...expanded].reverse().slice(1, -1);
			return [...expanded, ...down];
		}
		case "down-up": {
			if (expanded.length <= 1) return expanded;
			const reversed = [...expanded].reverse();
			const up = [...expanded].slice(1, -1);
			return [...reversed, ...up];
		}
		case "random":
			// Return the expanded set; the stepper will pick randomly
			return expanded;
		case "as-played": {
			// Use the original order (not sorted), expanded across octaves
			const asPlayed: number[] = [];
			for (let oct = 0; oct < octaves; oct++) {
				for (const note of heldNotes) {
					asPlayed.push(note + oct * 12);
				}
			}
			return asPlayed;
		}
		default:
			return expanded;
	}
}

/** The arpeggiator engine that manages timing and note cycling. */
export class Arpeggiator {
	private params: ArpParams = { ...DEFAULT_ARP };
	private bpm = 120;
	private heldNotes: number[] = [];
	private latchedNotes: number[] = [];
	private sequence: readonly number[] = [];
	private stepIndex = 0;
	private intervalId: ReturnType<typeof setInterval> | null = null;
	private currentPlayingNote: number | null = null;
	private noteOffTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private callback: ArpNoteCallback | null = null;

	/** Set the note callback (noteOn/noteOff). */
	setCallback(cb: ArpNoteCallback): void {
		this.callback = cb;
	}

	/** Update arpeggiator parameters. */
	setParams(update: Partial<ArpParams>): void {
		const wasEnabled = this.params.enabled;
		const wasLatch = this.params.latch;
		this.params = { ...this.params, ...update };

		// If latch was turned off, clear latched notes
		if (wasLatch && !this.params.latch) {
			this.latchedNotes = [];
			if (this.heldNotes.length === 0) {
				this.stopArp();
			}
		}

		if (!this.params.enabled && wasEnabled) {
			this.stopArp();
		}

		// Rebuild sequence and restart interval if rate/pattern/octaves changed
		if (this.params.enabled && this.isArpActive()) {
			this.rebuildSequence();
			this.restartInterval();
		}
	}

	/** Set the tempo (BPM). */
	setTempo(bpm: number): void {
		this.bpm = bpm;
		if (this.params.enabled && this.intervalId !== null) {
			this.restartInterval();
		}
	}

	/** Current arpeggiator parameters. */
	get currentParams(): Readonly<ArpParams> {
		return this.params;
	}

	/** Whether the arpeggiator is currently producing notes. */
	get isPlaying(): boolean {
		return this.intervalId !== null;
	}

	/** Note pressed by user. */
	noteOn(note: number): void {
		if (!this.params.enabled) return;

		// In latch mode, if note is already latched, remove it (toggle)
		if (this.params.latch) {
			const latchIdx = this.latchedNotes.indexOf(note);
			if (latchIdx !== -1 && !this.heldNotes.includes(note)) {
				this.latchedNotes.splice(latchIdx, 1);
				if (!this.isArpActive()) {
					this.stopArp();
				} else {
					this.rebuildSequence();
				}
				return;
			}
		}

		if (!this.heldNotes.includes(note)) {
			this.heldNotes.push(note);
		}

		if (this.params.latch && !this.latchedNotes.includes(note)) {
			this.latchedNotes.push(note);
		}

		this.rebuildSequence();

		if (this.intervalId === null && this.sequence.length > 0) {
			this.stepIndex = 0;
			this.step();
			this.restartInterval();
		}
	}

	/** Note released by user. */
	noteOff(note: number): void {
		if (!this.params.enabled) return;

		const idx = this.heldNotes.indexOf(note);
		if (idx !== -1) {
			this.heldNotes.splice(idx, 1);
		}

		if (!this.params.latch) {
			this.rebuildSequence();
			if (!this.isArpActive()) {
				this.stopArp();
			}
		}
	}

	/** Stop and release everything. */
	stop(): void {
		this.heldNotes = [];
		this.latchedNotes = [];
		this.stopArp();
	}

	/** Destroy the arpeggiator, cleaning up all state. */
	destroy(): void {
		this.stop();
		this.callback = null;
	}

	// -- Internal --

	private isArpActive(): boolean {
		const activeNotes = this.params.latch ? this.latchedNotes : this.heldNotes;
		return activeNotes.length > 0;
	}

	private rebuildSequence(): void {
		const activeNotes = this.params.latch ? this.latchedNotes : this.heldNotes;
		this.sequence = buildSequence(
			activeNotes,
			this.params.pattern,
			this.params.octaves,
		);
		// Clamp step index
		if (this.sequence.length > 0) {
			this.stepIndex = this.stepIndex % this.sequence.length;
		}
	}

	private step(): void {
		if (!this.callback || this.sequence.length === 0) return;

		// Release previous note
		if (this.currentPlayingNote !== null) {
			this.callback.noteOff(this.currentPlayingNote);
			this.currentPlayingNote = null;
		}
		if (this.noteOffTimeoutId !== null) {
			clearTimeout(this.noteOffTimeoutId);
			this.noteOffTimeoutId = null;
		}

		// Pick next note
		let note: number;
		if (this.params.pattern === "random") {
			const idx = Math.floor(Math.random() * this.sequence.length);
			note = this.sequence[idx] ?? this.sequence[0] ?? 60;
		} else {
			note = this.sequence[this.stepIndex % this.sequence.length] ?? 60;
			this.stepIndex = (this.stepIndex + 1) % this.sequence.length;
		}

		// Trigger note on
		this.callback.noteOn(note);
		this.currentPlayingNote = note;

		// Schedule note off based on gate length
		const stepMs = this.getStepMs();
		const gateMs = stepMs * this.params.gate;
		this.noteOffTimeoutId = setTimeout(() => {
			if (this.currentPlayingNote === note && this.callback) {
				this.callback.noteOff(note);
				this.currentPlayingNote = null;
			}
			this.noteOffTimeoutId = null;
		}, gateMs);
	}

	private getStepMs(): number {
		const beatDuration = 60000 / this.bpm; // ms per beat
		const multiplier = RATE_MULTIPLIERS[this.params.rate] ?? 0.5;
		return beatDuration * multiplier;
	}

	private restartInterval(): void {
		if (this.intervalId !== null) {
			clearInterval(this.intervalId);
		}
		const stepMs = this.getStepMs();
		this.intervalId = setInterval(() => this.step(), stepMs);
	}

	private stopArp(): void {
		if (this.intervalId !== null) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
		if (this.noteOffTimeoutId !== null) {
			clearTimeout(this.noteOffTimeoutId);
			this.noteOffTimeoutId = null;
		}
		if (this.currentPlayingNote !== null && this.callback) {
			this.callback.noteOff(this.currentPlayingNote);
			this.currentPlayingNote = null;
		}
		this.stepIndex = 0;
	}
}
